import { DeductionListJson } from "@/api"
import { BreakdownData } from "@/components/breakdown"
import { Badge } from "@/components/ui/badge"
import { TableState } from "@/deduction_table/table_state"
import { currencyFormatter, to_reporting_amount } from "@/utils"
import { notifyChartFilterApplied } from "@/utils/filter_feedback"
import { BarList } from "@tremor/react"
import { useMemo } from "preact/compat"

const CATEGORY_DOMAINS = [
  {
    name: "Sales",
    color: "emerald",
    categories: ["Slotting", "Free Fill", "Retailer Promotion", "Distributor Promotion"],
  },
  {
    name: "Operations",
    color: "sky",
    categories: ["Operational Issues", "Freight", "Spoilage"],
  },
  {
    name: "Finance",
    color: "amber",
    categories: ["Contractual Fees", "Invoice Discrepancy", "Misc", "Early Pay", "Repayment", "Finance"],
  },
]

/// category name (eg. Slotting) => [domainIndex, categoryIndex]
const CATEGORY_LOOKUP: Record<string, [number, number]> = Object.fromEntries(
  CATEGORY_DOMAINS.flatMap((domain, index) => {
    return domain.categories.map((category, categoryIndex) => [category, [index, categoryIndex]])
  })
)

// category name (eg. Slotting) => tailwind color (eg. emerald)
export const CATEGORY_COLORS = Object.fromEntries(
  CATEGORY_DOMAINS.flatMap(domain => domain.categories.map(category => [category, domain.color]))
)

interface CategoryBreakdownProps {
  data: DeductionListJson[]
}

/** Group deductions into the category domains for the breakdown chart. Renamed/custom
 * category names (not in the static domain map) land in an "Other" domain instead of
 * being dropped, so org-specific categories still show up and stay clickable. */
export function group_deductions_by_domain(rawData: DeductionListJson[]) {
  let groups = CATEGORY_DOMAINS.map(d => ({
    ...d,
    categories: d.categories.map(c => ({
      name: c,
      key: c,
      count: 0,
      value: 0,
      color: d.color,
    })),
    total: 0,
  }))
  const other = { name: "Other", color: "gray", categories: [] as (typeof groups)[number]["categories"], total: 0 }
  for (let row of rawData) {
    const lookup = CATEGORY_LOOKUP[row.category]
    let domain
    let category
    if (!lookup) {
      // renamed or org-custom category name
      domain = other
      category = other.categories.find(c => c.key === row.category)
      if (!category) {
        category = { name: row.category, key: row.category, count: 0, value: 0, color: other.color }
        other.categories.push(category)
      }
    } else {
      let [domainIndex, categoryIndex] = lookup
      domain = groups[domainIndex]
      category = domain.categories[categoryIndex]
    }
    category.count += 1
    // In dollars, so a row paid in another currency adds to the rest.
    const amount = to_reporting_amount(row, row.deduction_amount)
    category.value += amount
    domain.total += amount
  }
  if (other.categories.length > 0) {
    groups.push(other)
  }
  // strip out empty categories
  groups = groups
    .map(domain => ({
      ...domain,
      categories: domain.categories.filter(c => c.count > 0),
    }))
    .filter(domain => domain.categories.length > 0)
  for (let domain of groups) {
    for (let category of domain.categories) {
      category.name += ` (${category.count})`
    }
  }
  return groups
}

export function CategoryBreakdown({ data: rawData }: CategoryBreakdownProps) {
  let groupedData = useMemo(() => group_deductions_by_domain(rawData), [rawData])

  return (
    <div className="flex">
      <div className="mt-2 max-w-2xl grow">
        {groupedData.map(domain => (
          <div key={domain.name}>
            <div class="mb-2 flex items-center justify-between mt-6">
              <div class="flex items-center gap-2">
                <h3 class="font-medium">{domain.name}</h3>
                <Badge variant="outline">{currencyFormatter(domain.total)}</Badge>
              </div>
            </div>
            <BarList
              data={domain.categories}
              className="mt-2"
              valueFormatter={currencyFormatter}
              onValueChange={(item: BreakdownData) => {
                if (item.key === "Early Pay") {
                  TableState.set(s => ({
                    ...s,
                    search: "",
                    columnFilters: [],
                    pagination: { pageIndex: 0, pageSize: s.pagination.pageSize },
                    earlyPay: true,
                  }))
                  notifyChartFilterApplied("Showing early pay invoices.")
                } else {
                  TableState.set(s => ({
                    ...s,
                    search: "",
                    // transactionTypes: putTransactionType(s.transactionTypes, "deduction"),
                    earlyPay: false,
                    columnFilters: [{ id: "category", value: item.key }],
                    pagination: { pageIndex: 0, pageSize: s.pagination.pageSize },
                  }))
                  notifyChartFilterApplied(`Showing deductions in category "${item.key}".`)
                }
              }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  // Test the core logic without Preact hooks
  const testCategoryProcessing = group_deductions_by_domain

  describe("CategoryBreakdown", () => {
    describe("category lookup validation", () => {
      it("should have lookup entries for all known database categories", () => {
        const KNOWN_DATABASE_CATEGORIES = CATEGORY_DOMAINS.flatMap(domain => domain.categories)

        KNOWN_DATABASE_CATEGORIES.forEach(category => {
          expect(CATEGORY_LOOKUP[category]).toBeDefined()
          expect(Array.isArray(CATEGORY_LOOKUP[category])).toBe(true)
          expect(CATEGORY_LOOKUP[category]).toHaveLength(2)
        })
      })
    })

    describe("category processing", () => {
      it("should process all known database categories without throwing", () => {
        const KNOWN_DATABASE_CATEGORIES = CATEGORY_DOMAINS.flatMap(domain => domain.categories)

        // Create test data with all known categories
        const testData: DeductionListJson[] = KNOWN_DATABASE_CATEGORIES.map(
          (category, index) =>
            ({
              id: `test-${index}`,
              invoice_amount: -100.0,
              invoice_number: `INV-${index}`,
              category: category,
            }) as DeductionListJson
        )

        // This should not throw "undefined is not iterable" error
        expect(() => {
          testCategoryProcessing(testData)
        }).not.toThrow()
      })

      it("should group unknown (renamed/custom) categories into an Other domain", () => {
        const testData: DeductionListJson[] = [
          {
            id: "test-1",
            deduction_amount: -100.0,
            invoice_number: "INV-1",
            category: "Retailer Promo",
          } as unknown as DeductionListJson,
          {
            id: "test-2",
            deduction_amount: -50.0,
            invoice_number: "INV-2",
            category: "Retailer Promo",
          } as unknown as DeductionListJson,
          {
            id: "test-3",
            deduction_amount: -25.0,
            invoice_number: "INV-3",
            category: "Another Unknown",
          } as unknown as DeductionListJson,
        ]

        const groups = testCategoryProcessing(testData)
        const other = groups.find(g => g.name === "Other")
        expect(other).toBeDefined()
        expect(other!.categories.map(c => c.key)).toEqual(["Retailer Promo", "Another Unknown"])
        expect(other!.categories[0].count).toBe(2)
        expect(other!.categories[0].value).toBe(-150)
        expect(other!.total).toBe(-175)
      })

      it("should handle mixed known and unknown categories", () => {
        const testData: DeductionListJson[] = [
          {
            id: "test-1",
            invoice_amount: -100.0,
            invoice_number: "INV-1",
            category: "Slotting", // Known category
          } as DeductionListJson,
          {
            id: "test-2",
            invoice_amount: -100.0,
            invoice_number: "INV-2",
            category: "Unknown Category", // Unknown category
          } as DeductionListJson,
          {
            id: "test-3",
            invoice_amount: -100.0,
            invoice_number: "INV-3",
            category: "Freight", // Known category
          } as DeductionListJson,
        ]

        const groups = testCategoryProcessing(testData)
        // known categories land in their domains, the unknown one in Other
        const other = groups.find(g => g.name === "Other")
        expect(other!.categories.map(c => c.key)).toEqual(["Unknown Category"])
        const sales = groups.find(g => g.name === "Sales")
        expect(sales!.categories.some(c => c.key === "Slotting")).toBe(true)
      })

      it("should handle empty data gracefully", () => {
        expect(() => {
          testCategoryProcessing([])
        }).not.toThrow()
      })

      it("regression: should handle unknown categories without throwing (prevents original bug)", () => {
        const testData: DeductionListJson[] = [
          {
            id: "test-1",
            invoice_amount: -100.0,
            invoice_number: "INV-1",
            category: "Unknown Category",
          } as DeductionListJson,
          {
            id: "test-2",
            invoice_amount: -100.0,
            invoice_number: "INV-2",
            category: "Another Unknown",
          } as DeductionListJson,
        ]

        // This test would have caught the original "undefined is not iterable" bug
        // The original bug occurred when trying to destructure undefined from CATEGORY_LOOKUP
        expect(() => {
          testCategoryProcessing(testData)
        }).not.toThrow()
      })
    })
  })
}
